import { Roles } from "../../../../constants/roles";

import { normalizeTikTokVideoUrl } from "../../../extractor/tiktok-embed-extractor";

type TikTokEmbedOptions = {
    includeFallbackLink?: boolean;
    autoplay?: boolean;
};

function buildTikTokEmbedNode(url: string, options: TikTokEmbedOptions = {}): HTMLElement | null {
    try {
        if (!url || typeof url !== "string") return null;

        const permalink = normalizeTikTokVideoUrl(url) || String(url || "");
        if (!/tiktok\.com\//i.test(permalink)) return null;

        const idMatch = permalink.match(/\/video\/(\d+)/i);
        if (!idMatch?.[1]) return null;

        const videoId = idMatch[1];
        // Deliberately /player/v1/ (TikTok's documented Embed Player API),
        // not /embed/v2/ (TikTok's other, undocumented embed endpoint) —
        // /embed/v2/ was the source of the "overload-protect triggered"
        // errors reported in ISS-02-187.
        const iframeSrc = `https://www.tiktok.com/player/v1/${videoId}${options.autoplay ? "?autoplay=1" : ""}`;

        const container = document.createElement("div");
        container.className = "sns-embed tiktok-embed";
        container.setAttribute("data-role", Roles.SNS_EMBED);
        container.style.cssText = "margin-bottom: 16px; margin-top: 16px;";

        const wrapper = document.createElement("div");
        wrapper.style.cssText =
            "max-width: 360px; width: 100%; height: min(820px, 80vh); min-height: 640px; overflow: hidden; border-radius: 8px; background: #000;";

        const iframe = document.createElement("iframe");
        iframe.src = iframeSrc;
        iframe.title = "TikTok";
        iframe.loading = "lazy";
        iframe.allowFullscreen = true;
        iframe.setAttribute("frameborder", "0");
        iframe.setAttribute("scrolling", "no");
        iframe.setAttribute(
            "allow",
            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
        );
        iframe.style.cssText = "width: 100%; height: 100%; border: 0;";

        wrapper.appendChild(iframe);
        container.appendChild(wrapper);

        if (options.includeFallbackLink !== false) {
            const fallback = document.createElement("div");
            fallback.style.cssText = "margin-top: 8px;";
            const link = document.createElement("a");
            link.href = permalink;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.textContent = "View on TikTok";
            fallback.appendChild(link);
            container.appendChild(fallback);
        }

        return container;
    } catch (e) {
        console.error("buildTikTokEmbedNode failed", e);
        return null;
    }
}

export class TikTokEmbedRender {
    public static generateTikTokEmbed(url: string): HTMLElement | null {
        return buildTikTokEmbedNode(url);
    }

    public static generateTikTokEmbedForTile(url: string, options: TikTokEmbedOptions = {}): HTMLElement | null {
        return buildTikTokEmbedNode(url, { includeFallbackLink: false, autoplay: options.autoplay });
    }
}

export { buildTikTokEmbedNode };
